// src/encounter/lib/payload-to-seed.ts
// Reconstruct a dialog's initial (seed) values from a stored CodedOrderPayload — the inverse of the
// to-*-order mappers. Powers click-to-edit (PROD-1433): clicking a listed canvas order reopens its
// config dialog PRE-FILLED with the order's current values (Flutter's `originalData`), so an edit
// tweaks the order rather than rebuilding it from defaults. Pure transforms — no I/O.
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { CodedOrderPayload, OrderCoding } from './order-payload';
import type { MedicationPick, MedRoute } from './to-medication-result';
import type { MedicationDose } from './to-medication-order';
import type { VaccinationDraft, Coding } from '../vaccination-admin-editor';
import type { VaccineValueSet } from '@/shared/services/use-vaccination-search';
import type { ImmunizationCoding } from '@/types/patient-chart-api';
import type { AssessmentCoding, AssessmentPlanVaccination } from '@/types/assessment-api';

/** OrderCoding (code+displayName+system) → DS CodeSearchResult (id=code so the picker keys stably). */
export function codingToCodeSearchResult(c: OrderCoding): CodeSearchResult {
  return {
    id: c.code || c.displayName,
    ...(c.code ? { code: c.code } : {}),
    display: c.displayName || c.code,
    ...(c.system ? { system: c.system } : {}),
  };
}

/** lab / imaging / procedure payload → the picked CodeSearchResult (seeds the dialog's selection). */
export function payloadToCodeSearchResult(
  payload: CodedOrderPayload,
): CodeSearchResult | undefined {
  if (payload.kind === 'lab' || payload.kind === 'imaging' || payload.kind === 'procedure') {
    return codingToCodeSearchResult(payload.code);
  }
  return undefined;
}

/** medication payload → the MedicationPick the dose dialog opens with (identity + route/uom). Route/uom
 *  present ⇒ the dialog skips the by-code lookup (edit seed wins, Flutter originalData). */
export function payloadToMedicationPick(
  payload: Extract<CodedOrderPayload, { kind: 'medication' }>,
): MedicationPick {
  const route: MedRoute[] | undefined = payload.route
    ? [{ code: payload.route.code, display: payload.route.displayName, ...(payload.route.system ? { system: payload.route.system } : {}) }]
    : undefined;
  return {
    id: payload.medicationCode.code || payload.medicationCode.displayName,
    name: payload.medicationCode.displayName,
    ...(payload.medicationCode.code ? { code: payload.medicationCode.code } : {}),
    // The saved order's coding system, so re-confirming an edit keeps it (PROD-2831). A legacy
    // order saved with none opens without one, and stays without one.
    ...(payload.medicationCode.system?.trim() ? { system: payload.medicationCode.system.trim() } : {}),
    ...(route ? { route } : {}),
    ...(payload.doseUnit ? { unitOfMeasurement: payload.doseUnit } : {}),
  };
}

/** medication payload → the full dose seed (frequency / schedule / dispense / sig / …) for edit. */
export function payloadToMedicationDose(
  payload: Extract<CodedOrderPayload, { kind: 'medication' }>,
): MedicationDose {
  return {
    // Carry persisted alerts into the edit seed, so a re-confirm preserves them rather than blanking
    // the record of a warning (PROD-1801).
    ...(payload.vidalAlerts?.length ? { vidalAlerts: payload.vidalAlerts } : {}),
    ...(payload.route ? { route: payload.route.code, routeDisplay: payload.route.displayName } : {}),
    ...(payload.doseUnit ? { doseUnit: payload.doseUnit } : {}),
    ...(payload.frequency ? { frequency: payload.frequency } : {}),
    ...(payload.quantity !== undefined ? { quantity: payload.quantity } : {}),
    ...(payload.timesADay !== undefined ? { timesADay: payload.timesADay } : {}),
    ...(payload.daysInterval !== undefined ? { daysInterval: payload.daysInterval } : {}),
    ...(payload.specificDays ? { specificDays: payload.specificDays } : {}),
    ...(payload.startDate ? { startDate: payload.startDate } : {}),
    ...(payload.duration !== undefined ? { duration: payload.duration } : {}),
    ...(payload.durationUnit ? { durationUnit: payload.durationUnit } : {}),
    ...(payload.dispense !== undefined ? { dispense: payload.dispense } : {}),
    ...(payload.dispenseUnit ? { dispenseUnit: payload.dispenseUnit } : {}),
    ...(payload.refill !== undefined ? { refill: payload.refill } : {}),
    ...(payload.substitution !== undefined ? { substitution: payload.substitution } : {}),
    ...(payload.sig ? { sig: payload.sig } : {}),
    ...(payload.note ? { note: payload.note } : {}),
    ...(payload.weight !== undefined ? { weight: payload.weight } : {}),
    ...(payload.priority ? { priority: payload.priority } : {}),
  };
}

/** 6-field wire coding → the editor's 3-field Coding (inverse of to-vaccination-entry.code6). */
function toCoding(c: AssessmentCoding | null | undefined): Coding | null {
  return c && c.code ? { code: c.code, display: c.display ?? c.code, system: c.system ?? '' } : null;
}

/** no-Z ISO (or any date string) → YYYY-MM-DD display value (the editor's date input value). */
function dateDisplay(iso: string | null | undefined): string {
  return iso ? iso.slice(0, 10) : '';
}

/** vaccination entry → the coding the VaccineAddDialog opens with (skips the search step). */
export function vaccinationEntryToCoding(entry: AssessmentPlanVaccination): ImmunizationCoding {
  const v = entry.vaccine;
  return {
    code: v?.code ?? null,
    display: v?.display ?? null,
    system: v?.system ?? null,
    Definition: v?.Definition ?? null,
  };
}

/**
 * vaccination entry → the VaccineValueSet the admin editor edits (PROD-1433). The editor rebuilds the
 * saved `route` on submit from `vaccine.route` (not the draft), so an edit MUST seed the value-set's
 * `route` with the saved route — otherwise `codingToValueSet` would leave it `[]` and the route would
 * be dropped on save (Codex High). `unitOfMeasurement` seeds the admin/dose unit defaults.
 */
export function vaccinationEntryToValueSet(entry: AssessmentPlanVaccination): VaccineValueSet {
  const v = entry.vaccine;
  const r = entry.route;
  return {
    valueCoding: {
      code: v?.code ?? '',
      display: v?.display ?? v?.code ?? '',
      // The saved entry's system, or blank: never a guessed 'DDC' (PROD-2831, the PROD-1819 rule).
      // A CVX-coded entry saved with no system must not come back labelled DDC on its next save.
      system: (v?.system ?? '').trim(),
    },
    unitOfMeasurement: entry.administrationDoseUnit?.code ?? entry.administrationUnit?.code ?? null,
    route: r && r.code ? [{ code: r.code, display: r.display ?? r.code, system: r.system ?? '' }] : [],
  };
}

/** vaccination entry → the admin-editor draft seed (inverse of vaccinationDraftToEntry). */
export function vaccinationEntryToDraft(entry: AssessmentPlanVaccination): Partial<VaccinationDraft> {
  return {
    administratedDate: entry.administratedDate ?? null,
    administratedDisplay: dateDisplay(entry.administratedDate),
    lotNo: entry.lotNo ?? '',
    expiryDate: entry.expiryDate ?? null,
    expiryDisplay: dateDisplay(entry.expiryDate),
    site: toCoding(entry.site),
    route: toCoding(entry.route),
    repeatPattern: toCoding(entry.repeatPattern),
    administrationAmount: entry.administrationAmount ?? null,
    administrationUnit: entry.administrationUnit?.code ?? null,
    administrationDoseQuantity: entry.administrationDoseQuantity ?? null,
    administrationDoseUnit: entry.administrationDoseUnit?.code ?? null,
    durationQuantity: toCoding(entry.durationQuantity),
    durationUnits: toCoding(entry.durationUnits),
    remarks: entry.remarks ?? '',
    ...(entry.isAdministrationRequested != null ? { isAdministrationRequested: entry.isAdministrationRequested } : {}),
  };
}
